/**
 * @fileoverview TipTap Collection Extensions (MOR-708 template collections)
 *
 * Four nodes that let one template adapt to unknown cardinality — multiple
 * directors, a corporate group's security interests, several signatories —
 * without per-count template variants:
 *
 *  - repeatBlock      block container stamped once per collection item at
 *                     render time. Item values are inserted inside it via
 *                     ordinary field chips with `item_<part>` keys.
 *  - conditionBlock   block container kept only when the collection count
 *                     falls within [min, max] at render time.
 *  - collectionJoin   inline atom → grammatical list ("A, B and C").
 *  - collectionPlural inline atom → singular/plural text picked by count
 *                     (supports a literal `{count}` token).
 *
 * Serialisation is plain HTML data attributes, matching what the expansion
 * engine in convex/jobs/precedents/collectionsShared.ts consumes:
 *
 *   <div data-type="repeat-block" data-collection="security_interests">…</div>
 *   <div data-type="condition-block" data-collection="directors"
 *        data-min="2">…</div>
 *   <span data-type="collection-join" data-collection="directors"
 *         data-part="name" data-final="and">…</span>
 *   <span data-type="collection-plural" data-collection="directors"
 *         data-one="director is" data-many="directors are">…</span>
 */

import { Extension, mergeAttributes, Node as TiptapNode } from "@tiptap/core";
import type { NodeViewRendererProps } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { ViewMutationRecord } from "@tiptap/pm/view";
import { TEMPLATE_COLLECTIONS_BY_KEY } from "@cvx/jobs/precedents/collectionsShared";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    collectionBlocks: {
      insertRepeatBlock: (attrs: {
        collection: string;
        /**
         * Item fields to seed the block with, so a fresh repeat shows a
         * working example instead of an empty box. `inline` (default)
         * puts them in one paragraph separated by " — "; `stacked` puts
         * one per paragraph (signature above name, etc.).
         */
        seedFields?: Array<{ part: string; label: string }>;
        seedLayout?: "inline" | "stacked";
        /**
         * `columns` lays the repeated items side by side (one table cell
         * per item at render time) — built for multi-signer signature
         * blocks. Default stacks them vertically.
         */
        mode?: "columns";
      }) => ReturnType;
      insertConditionBlock: (attrs: {
        collection: string;
        min?: number;
        max?: number;
      }) => ReturnType;
      insertCollectionJoin: (attrs: {
        collection: string;
        part?: string;
        final?: string;
      }) => ReturnType;
      insertCollectionPlural: (attrs: {
        collection: string;
        one: string;
        many: string;
      }) => ReturnType;
    };
  }
}

function collectionLabel(key: string | null | undefined): string {
  if (!key) return "collection";
  return TEMPLATE_COLLECTIONS_BY_KEY.get(key)?.label ?? key.replace(/_/g, " ");
}

/** Human label for one item field (e.g. "ppsr_number" → "PPSR Registration Number"). */
function fieldLabel(
  collectionKey: string | null | undefined,
  part: string | null | undefined,
): string {
  if (!part) return "Name";
  const meta = collectionKey
    ? TEMPLATE_COLLECTIONS_BY_KEY.get(collectionKey)
    : undefined;
  return (
    meta?.fields.find((f) => f.part === part)?.label ??
    part.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * Shared container NodeView: dashed bordered box with a small uppercase
 * header label on its own row, body remains fully editable below.
 *
 * Uses a stacked label + content layout (not absolute positioning with
 * padding-top) because `.prose-letter > :first-child` zeroes padding-top
 * with `!important`, which caused the header to overlap inline field chips.
 *
 * The header row is a flex container: label on the left, an "×" delete
 * button on the right (mirrors the variant block's affordance so authors
 * have one consistent way to remove editor blocks — MOR / July 2026).
 */
function containerNodeView(
  labelText: string,
  accent: string,
  props: NodeViewRendererProps,
) {
  const dom = document.createElement("div");
  dom.className = "collection-block-editor";
  dom.style.border = `1.5px dashed ${accent}`;
  dom.style.borderRadius = "6px";
  dom.style.padding = "6px 10px 8px";
  dom.style.margin = "6px 0";

  const header = document.createElement("div");
  header.className = "collection-block-editor__header";
  header.contentEditable = "false";
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.justifyContent = "space-between";
  header.style.gap = "8px";
  header.style.marginBottom = "6px";
  header.style.userSelect = "none";

  const label = document.createElement("span");
  label.className = "collection-block-editor__label";
  label.textContent = labelText;
  label.style.fontSize = "11px";
  label.style.fontWeight = "600";
  label.style.color = accent;
  label.style.lineHeight = "1.4";
  header.appendChild(label);

  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.className = "collection-block-editor__delete";
  deleteBtn.setAttribute("contenteditable", "false");
  deleteBtn.setAttribute("title", "Remove multi-field block");
  deleteBtn.setAttribute("aria-label", "Remove multi-field block");
  deleteBtn.innerHTML = "×";
  deleteBtn.addEventListener("mousedown", (e) => {
    // mousedown (not click) + preventDefault so ProseMirror doesn't move
    // the selection into the block before we delete it — same trick the
    // variant block uses.
    e.preventDefault();
    e.stopPropagation();
    const { editor, getPos, node } = props;
    if (!editor) return;
    const pos = typeof getPos === "function" ? getPos() : null;
    if (typeof pos !== "number") return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  });
  header.appendChild(deleteBtn);

  dom.appendChild(header);

  const contentDOM = document.createElement("div");
  contentDOM.className = "collection-block-editor__content";
  dom.appendChild(contentDOM);
  return {
    dom,
    contentDOM,
    // Keep header events (delete click) out of ProseMirror's hands so it
    // doesn't reinterpret them as editor clicks / selection changes.
    stopEvent(event: Event) {
      return header.contains(event.target as Node);
    },
    ignoreMutation(mutation: ViewMutationRecord) {
      return header.contains(mutation.target as Node);
    },
  };
}

// ---------------------------------------------------------------------------
// repeatBlock
// ---------------------------------------------------------------------------

export const RepeatBlock = TiptapNode.create({
  name: "repeatBlock",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      collection: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-collection"),
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-collection": attrs.collection,
        }),
      },
      mode: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-mode"),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.mode ? { "data-mode": attrs.mode } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="repeat-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "repeat-block" }),
      0,
    ];
  },

  addCommands() {
    return {
      insertRepeatBlock:
        (attrs) =>
        ({ commands }) => {
          const fields = attrs.seedFields ?? [];
          const chip = (field: { part: string; label: string }) => ({
            type: "fieldPlaceholder",
            attrs: { key: `item_${field.part}`, label: field.label },
          });
          const content =
            fields.length === 0
              ? [{ type: "paragraph" }]
              : attrs.seedLayout === "stacked"
                ? fields.map((field) => ({
                    type: "paragraph",
                    content: [chip(field)],
                  }))
                : [
                    {
                      type: "paragraph",
                      content: fields.flatMap((field, i) =>
                        i === 0
                          ? [chip(field)]
                          : [{ type: "text", text: " — " }, chip(field)],
                      ),
                    },
                  ];
          return commands.insertContent({
            type: this.name,
            attrs: {
              collection: attrs.collection,
              mode: attrs.mode ?? null,
            },
            content,
          });
        },
    };
  },

  addNodeView() {
    return (props: NodeViewRendererProps) =>
      containerNodeView(
        props.node.attrs.mode === "columns"
          ? `Repeated side by side: ${collectionLabel(props.node.attrs.collection as string)}`
          : `Repeated for each: ${collectionLabel(props.node.attrs.collection as string)}`,
        "#0e7490",
        props,
      );
  },
});

// ---------------------------------------------------------------------------
// conditionBlock
// ---------------------------------------------------------------------------

export const ConditionBlock = TiptapNode.create({
  name: "conditionBlock",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      collection: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-collection"),
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-collection": attrs.collection,
        }),
      },
      min: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-min"),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.min == null || attrs.min === ""
            ? {}
            : { "data-min": String(attrs.min) },
      },
      max: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-max"),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.max == null || attrs.max === ""
            ? {}
            : { "data-max": String(attrs.max) },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="condition-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "condition-block" }),
      0,
    ];
  },

  addCommands() {
    return {
      insertConditionBlock:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              collection: attrs.collection,
              min: attrs.min ?? null,
              max: attrs.max ?? null,
            },
            content: [{ type: "paragraph" }],
          }),
    };
  },

  addNodeView() {
    return (props: NodeViewRendererProps) => {
      const label = collectionLabel(props.node.attrs.collection as string);
      const min = props.node.attrs.min as string | number | null;
      const max = props.node.attrs.max as string | number | null;
      const bound =
        min != null && max != null
          ? `${min}–${max}`
          : max != null
            ? `≤ ${max}`
            : `≥ ${min ?? 0}`;
      return containerNodeView(`Show when ${label} ${bound}`, "#b45309", props);
    };
  },
});

// ---------------------------------------------------------------------------
// collectionJoin
// ---------------------------------------------------------------------------

export const CollectionJoin = TiptapNode.create({
  name: "collectionJoin",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  marks: "_",

  addAttributes() {
    return {
      collection: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-collection"),
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-collection": attrs.collection,
        }),
      },
      part: {
        default: "name",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-part") ?? "name",
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-part": attrs.part ?? "name",
        }),
      },
      final: {
        default: "and",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-final") ?? "and",
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-final": attrs.final ?? "and",
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="collection-join"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    // Say exactly WHAT gets listed and HOW: collection, field, and the
    // join style, e.g. "All Security Interests — PPSR Registration
    // Numbers: A, B and C" (or "A, B, C" for the comma-only style).
    const cLabel = collectionLabel(node.attrs.collection as string);
    // `part` may combine fields with "+" (first_name+last_name) — label
    // each and join with " + " so the chip reads "First Name + Last Name".
    const fLabel = ((node.attrs.part as string) || "name")
      .split("+")
      .map((p) => fieldLabel(node.attrs.collection as string, p))
      .join(" + ");
    const final = (node.attrs.final as string) ?? "and";
    const example =
      final === "none" || final.trim() === "" ? "A, B, C" : `A, B ${final} C`;
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-type": "collection-join",
        class: "field-placeholder-chip",
        title: `Replaced with every ${cLabel.toLowerCase()} ${fLabel.toLowerCase()} joined like “${example}” when the document is generated`,
      }),
      `All ${cLabel} — ${fLabel}s: ${example}`,
    ];
  },

  addCommands() {
    return {
      insertCollectionJoin:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              collection: attrs.collection,
              part: attrs.part ?? "name",
              final: attrs.final ?? "and",
            },
          }),
    };
  },
});

// ---------------------------------------------------------------------------
// collectionPlural
// ---------------------------------------------------------------------------

export const CollectionPlural = TiptapNode.create({
  name: "collectionPlural",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  marks: "_",

  addAttributes() {
    return {
      collection: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-collection"),
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-collection": attrs.collection,
        }),
      },
      one: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-one") ?? "",
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-one": attrs.one ?? "",
        }),
      },
      many: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-many") ?? "",
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-many": attrs.many ?? "",
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="collection-plural"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const one = (node.attrs.one as string) || "…";
    const many = (node.attrs.many as string) || "…";
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-type": "collection-plural",
        class: "field-placeholder-chip",
        title: `Becomes “${one}” when there is one ${collectionLabel(node.attrs.collection as string).toLowerCase()}, “${many}” otherwise ({count} = the number)`,
      }),
      `${one} ⁄ ${many}`,
    ];
  },

  addCommands() {
    return {
      insertCollectionPlural:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs,
          }),
    };
  },
});

// ---------------------------------------------------------------------------
// Repeating list items
// ---------------------------------------------------------------------------

/**
 * Bulleted / numbered collection lists reuse the CORE listItem node — this
 * extension just teaches it to carry (and round-trip) the repeat markers:
 *
 *   <ul><li data-type="repeat-item" data-collection="security_interests">
 *     …item chips, any combination…
 *   </li></ul>
 *
 * At render time the expansion engine stamps the <li> once per item, so a
 * bulleted list gets one bullet per security interest and a numbered list
 * numbers itself 1..N naturally.
 */
const collectionListItemDeletePluginKey = new PluginKey(
  "collectionListItemDelete",
);

export const CollectionListItem = Extension.create({
  name: "collectionListItem",
  addGlobalAttributes() {
    return [
      {
        types: ["listItem"],
        attributes: {
          repeatType: {
            default: null,
            parseHTML: (el: HTMLElement) => el.getAttribute("data-type"),
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.repeatType ? { "data-type": attrs.repeatType } : {},
          },
          repeatCollection: {
            default: null,
            parseHTML: (el: HTMLElement) => el.getAttribute("data-collection"),
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.repeatCollection
                ? { "data-collection": attrs.repeatCollection }
                : {},
          },
        },
      },
    ];
  },

  /**
   * Inject a floating "×" delete button on every `repeat-item` `<li>` so
   * authors can remove a multi-field list entry with one click — matching
   * the affordance the repeat/condition block containers already carry.
   *
   * Implemented as a ProseMirror widget decoration (rather than a NodeView)
   * so we don't have to replace the core `listItem` NodeView globally.
   * The button re-resolves the list-item's current position at click time
   * via `view.posAtDOM`, so it stays correct as the doc mutates.
   */
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: collectionListItemDeletePluginKey,
        props: {
          decorations(state) {
            const widgets: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (
                node.type.name === "listItem" &&
                node.attrs.repeatType === "repeat-item"
              ) {
                widgets.push(
                  Decoration.widget(
                    pos + 1,
                    () => {
                      const btn = document.createElement("button");
                      btn.type = "button";
                      btn.className =
                        "collection-block-editor__delete collection-block-editor__delete--list";
                      btn.setAttribute("contenteditable", "false");
                      btn.setAttribute("title", "Remove multi-field item");
                      btn.setAttribute("aria-label", "Remove multi-field item");
                      btn.innerHTML = "×";
                      btn.addEventListener("mousedown", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const li = btn.closest(
                          'li[data-type="repeat-item"]',
                        ) as HTMLElement | null;
                        if (!li) return;
                        // Re-derive the li's current position; the pos
                        // captured at decoration-build time is stale after
                        // any doc mutation.
                        const currentPos = editor.view.posAtDOM(li, 0);
                        if (currentPos < 0) return;
                        const $pos = editor.state.doc.resolve(currentPos);
                        // posAtDOM(li, 0) lands INSIDE the li at depth+1;
                        // walk up one node to the li itself.
                        const liNode = $pos.node($pos.depth);
                        if (liNode?.type.name !== "listItem") return;
                        const from = $pos.before($pos.depth);
                        const to = from + liNode.nodeSize;
                        editor.chain().focus().deleteRange({ from, to }).run();
                      });
                      return btn;
                    },
                    { side: -1, ignoreSelection: true },
                  ),
                );
              }
              return true;
            });
            return DecorationSet.create(state.doc, widgets);
          },
        },
      }),
    ];
  },
});

export const collectionExtensions = [
  RepeatBlock,
  ConditionBlock,
  CollectionJoin,
  CollectionPlural,
  CollectionListItem,
];
